// Tap-to-identify card (2026-10-02): the same card from the AR view, the
// radar, the map and the list - type, network, material, diameter, ID,
// distance and the register depth label - so a crew can tell a water main
// from a wastewater pipe on the spot.
import { h } from "./dom";
import { NETWORK_COLOURS, NETWORK_LABELS, TYPE_LABELS, assetNetwork, symbolIcon } from "./assetStyle";

export interface IdentifyInfo {
  asset_id: string;
  asset_type: string;
  label?: string | null;
  network?: string | null;
  water_type?: string | null;
  type_label?: string | null;
  material?: string | null;
  diameter_mm?: number | null;
  distance_m?: number | null;
  depth_label?: string | null;
  address?: string | null;
  source?: string;
  attribution?: string;
  uncertainty_m?: number | null;
  direction?: string | null;
}

export function identifyCard(info: IdentifyInfo, actions: HTMLElement[] = [], onClose?: () => void,
  opts: { compact?: boolean } = {}): HTMLElement {
  const net = assetNetwork(info);
  const typeLabel = info.type_label || TYPE_LABELS[info.asset_type] || "Asset";
  if (opts.compact) return compactCard(info, net, typeLabel, actions, onClose);
  const rows: [string, string | null][] = [
    ["Network", NETWORK_LABELS[net]],
    ["Material", info.material ?? null],
    ["Diameter", info.diameter_mm ? `${Math.round(info.diameter_mm)} mm` : null],
    ["Depth", info.depth_label ?? "not recorded"],
    ["Distance", info.distance_m !== null && info.distance_m !== undefined
      ? `${info.direction ?? `${Math.round(info.distance_m)} m`}${info.uncertainty_m ? ` (could be ±${Math.round(info.uncertainty_m)} m out)` : ""}` : null],
    ["Address", info.address ?? null],
    ["Asset ID", info.asset_id],
  ];
  return h("div", { class: "fa-idcard", role: "dialog", "aria-label": `${typeLabel} details` },
    h("div", { class: "fa-idcard-head" },
      symbolIcon(info, 22),
      h("div", {},
        h("strong", {}, typeLabel),
        h("span", { class: "fa-net-chip", style: `background:${NETWORK_COLOURS[net]}` }, NETWORK_LABELS[net])),
      onClose ? h("button", { class: "fa-link fa-idcard-close", "aria-label": "Close", onclick: () => onClose() }, "✕") : null),
    h("dl", { class: "fa-idcard-rows" },
      ...rows.filter(([, v]) => v).flatMap(([k, v]) => [h("dt", {}, k), h("dd", {}, v as string)])),
    info.source === "test"
      ? h("p", { class: "fa-muted fa-small" }, `[TEST] demo data. ${info.attribution ?? ""}`)
      : null,
    actions.length ? h("div", { class: "fa-idcard-actions" }, ...actions) : null,
  );
}

/** The AR view's card (2026-10-06: the full card covered half the camera):
 * the type, network and distance in one head row, one summary line
 * (material · diameter · depth), and the rest behind "Details". */
function compactCard(info: IdentifyInfo, net: keyof typeof NETWORK_LABELS, typeLabel: string, actions: HTMLElement[],
  onClose?: () => void): HTMLElement {
  const dist = info.distance_m !== null && info.distance_m !== undefined
    ? `${Math.round(info.distance_m)} m${info.uncertainty_m ? ` (±${Math.round(info.uncertainty_m)} m)` : ""}` : null;
  const summary = [info.material, info.diameter_mm ? `${Math.round(info.diameter_mm)} mm` : null,
    info.depth_label ? `depth ${info.depth_label}` : "depth not recorded"].filter(Boolean).join(" · ");
  const rows: [string, string | null][] = [
    ["Address", info.address ?? null],
    ["Asset ID", info.asset_id],
    ["Source", info.source === "test" ? `[TEST] demo data. ${info.attribution ?? ""}` : info.attribution ?? null],
  ];
  const more = h("dl", { class: "fa-idcard-rows", hidden: true },
    ...rows.filter(([, v]) => v).flatMap(([k, v]) => [h("dt", {}, k), h("dd", {}, v as string)]));
  const toggle = h("button", { class: "fa-link fa-small", "aria-expanded": "false" }, "Details") as HTMLButtonElement;
  toggle.addEventListener("click", () => {
    more.hidden = !more.hidden;
    toggle.setAttribute("aria-expanded", more.hidden ? "false" : "true");
    toggle.textContent = more.hidden ? "Details" : "Less";
  });
  return h("div", { class: "fa-idcard fa-idcard-compact", role: "dialog", "aria-label": `${typeLabel} details` },
    h("div", { class: "fa-idcard-head" },
      symbolIcon(info, 20),
      h("div", {},
        h("strong", {}, typeLabel),
        h("span", { class: "fa-small" },
          h("span", { class: "fa-net-chip", style: `background:${NETWORK_COLOURS[net]}` }, NETWORK_LABELS[net]),
          dist ? ` ${dist}` : "")),
      onClose ? h("button", { class: "fa-link fa-idcard-close", "aria-label": "Close", onclick: () => onClose() }, "✕") : null),
    h("div", { class: "fa-row fa-small" }, h("span", {}, summary), toggle),
    more,
    actions.length ? h("div", { class: "fa-idcard-actions" }, ...actions) : null,
  );
}
